import { Logo } from "@/components/logo";
import {
  Button,
  type ButtonHierarchy,
  type ButtonSize,
  type ButtonState,
} from "@/components/button";
import { Field, type FieldState } from "@/components/field";
import { Toggle } from "@/components/toggle";
import { Tag, type TagSize, type TagTone } from "@/components/tag";
import { ArrowLeft, ArrowRight, Icon } from "@/components/icons";

type Swatch = {
  name: string;
  className: string;
  hex: string;
};

const NEUTRALS: Swatch[] = [
  { name: "black", className: "bg-black", hex: "#2D343C" },
  { name: "grey-950", className: "bg-grey-950", hex: "#384049" },
  { name: "grey-900", className: "bg-grey-900", hex: "#4E5966" },
  { name: "grey-600", className: "bg-grey-600", hex: "#73808E" },
  { name: "grey-500", className: "bg-grey-500", hex: "#A7B0BA" },
  { name: "grey-300", className: "bg-grey-300", hex: "#D9E0E8" },
  { name: "grey-200", className: "bg-grey-200", hex: "#E5EBF1" },
  { name: "grey-100", className: "bg-grey-100", hex: "#F2F5F9" },
  { name: "grey-50", className: "bg-grey-50", hex: "#F6F8FA" },
  { name: "grey-25", className: "bg-grey-25", hex: "#FBFBFC" },
  { name: "white", className: "bg-white", hex: "#FFFFFF" },
];

const GREENS: Swatch[] = [
  { name: "green-900", className: "bg-green-900", hex: "#405200" },
  { name: "green-700", className: "bg-green-700", hex: "#759507" },
  { name: "green-600", className: "bg-green-600", hex: "#88AD0C" },
  { name: "green-500", className: "bg-green-500", hex: "#95BC0E" },
  { name: "green-400", className: "bg-green-400", hex: "#AAD21F" },
  { name: "green-300", className: "bg-green-300", hex: "#D6FB55" },
  { name: "green-200", className: "bg-green-200", hex: "#F4FDD3" },
  { name: "green-100", className: "bg-green-100", hex: "#FBFFEF" },
];

const GREENISH_GREYS: Swatch[] = [
  { name: "greenish-grey-200", className: "bg-greenish-grey-200", hex: "#E8ECDA" },
];

const REDS: Swatch[] = [
  { name: "red-600", className: "bg-red-600", hex: "#F74D4D" },
  { name: "red-500", className: "bg-red-500", hex: "#FF6969" },
  { name: "red-400", className: "bg-red-400", hex: "#FF7E7E" },
  { name: "red-100", className: "bg-red-100", hex: "#FFF2F2" },
];

const YELLOWS: Swatch[] = [{ name: "yellow-500", className: "bg-yellow-500", hex: "#F6B733" }];

const ALPHAS: Swatch[] = [
  { name: "grey-900-24", className: "bg-grey-900-24", hex: "#4E5966 · 24%" },
  { name: "grey-500-32", className: "bg-grey-500-32", hex: "#A7B0BA · 32%" },
  { name: "green-500-12", className: "bg-green-500-12", hex: "#95BC0E · 12%" },
];

type TypeRow = {
  label: string;
  className: string;
  spec: string;
};

type Effect = {
  name: string;
  kind: "shadow" | "background blur";
  className: string;
  spec: string[];
};

const BUTTON_HIERARCHIES: ButtonHierarchy[] = [
  "primary",
  "secondary",
  "tertiary",
  "destructive",
  "link",
];
const BUTTON_STATES: ButtonState[] = ["default", "hover", "pressed", "disabled"];

const FIELD_STATES: FieldState[] = ["default", "hover", "focused", "filled", "disabled", "error"];

const EFFECTS: Effect[] = [
  {
    name: "flat",
    kind: "shadow",
    className: "shadow-flat",
    spec: ["x: 0", "y: 1", "blur: 3", "#181D22 · 8%"],
  },
  {
    name: "flat-black",
    kind: "shadow",
    className: "shadow-flat-black",
    spec: ["x: 0", "y: 1", "blur: 3", "#2D343C · 32%"],
  },
  {
    name: "flat-green",
    kind: "shadow",
    className: "shadow-flat-green",
    spec: ["x: 0", "y: 1", "blur: 3", "#759507 · 32%"],
  },
  {
    name: "flat-red",
    kind: "shadow",
    className: "shadow-flat-red",
    spec: ["x: 0", "y: 1", "blur: 3", "#F74D4D · 32%"],
  },
  {
    name: "soft",
    kind: "shadow",
    className: "shadow-soft",
    spec: ["x: 0", "y: 8", "blur: 32", "#2D343C · 8%"],
  },
  {
    name: "soft-black",
    kind: "shadow",
    className: "shadow-soft-black",
    spec: ["x: 0", "y: 8", "blur: 32", "#181D22 · 48%"],
  },
  {
    name: "glow",
    kind: "shadow",
    className: "shadow-glow",
    spec: ["x: 2", "y: 0", "blur: 12", "#DEFF6A"],
  },
  { name: "bg-blur", kind: "background blur", className: "backdrop-blur-bg", spec: ["blur: 32"] },
];

const TYPE_ROWS: TypeRow[] = [
  { label: "Heading 1", className: "text-h1", spec: "48 / 56 · Bold" },
  { label: "Heading 2", className: "text-h2", spec: "32 / 40 · Bold" },
  { label: "Heading 3", className: "text-h3", spec: "24 / 32 · Bold" },
  { label: "Heading 4", className: "text-h4", spec: "20 / 24 · Semibold" },
  { label: "body", className: "text-body", spec: "16 / 24 · Regular" },
  { label: "small", className: "text-small", spec: "14 / 20 · Regular" },
  { label: "tiny", className: "text-tiny", spec: "12 / 16 · Regular" },
];

function SwatchCard({ swatch, light }: { swatch: Swatch; light?: boolean }) {
  return (
    <div className="space-y-2">
      <div
        className={`${swatch.className} h-20 w-full rounded-lg ${
          light ? "border border-grey-200" : ""
        }`}
        aria-hidden
      />
      <div className="space-y-0.5">
        <div className="text-tiny text-black">{swatch.name}</div>
        <div className="text-tiny text-grey-500">{swatch.hex}</div>
      </div>
    </div>
  );
}

function EffectCard({ effect }: { effect: Effect }) {
  return (
    <div className="flex items-start gap-6">
      <div className="shrink-0 p-4">
        {effect.kind === "background blur" ? (
          <div className="relative h-24 w-24 overflow-hidden rounded-[4px] bg-grey-300">
            <div className={`absolute inset-0 ${effect.className}`} />
          </div>
        ) : (
          <div className={`h-24 w-24 rounded-[4px] bg-white ${effect.className}`} />
        )}
      </div>
      <div className="flex-1 space-y-1 pt-2">
        <p className="text-small text-grey-500">{effect.kind}</p>
        <h4 className="text-h4 text-black">&ldquo;{effect.name}&rdquo;</h4>
        <ul className="mt-2 space-y-0.5">
          {effect.spec.map((line) => (
            <li key={line} className="text-small text-grey-900">
              {line}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ColorRow({ title, swatches }: { title: string; swatches: Swatch[] }) {
  return (
    <div className="space-y-3">
      <h3 className="text-small text-grey-900">{title}</h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-10">
        {swatches.map((s) => {
          const light = [
            "grey-200",
            "grey-100",
            "grey-50",
            "grey-25",
            "white",
            "green-100",
            "red-100",
          ].includes(s.name);
          return <SwatchCard key={s.name} swatch={s} light={light} />;
        })}
      </div>
    </div>
  );
}

export default function DesignPage() {
  return (
    <main className="min-h-screen bg-white text-black">
      <div className="mx-auto flex max-w-6xl flex-col gap-2 p-4">
        <header className="border-b border-grey-200 pb-8">
          <h1 className="text-h1">Design System</h1>
          <p className="mt-3 max-w-2xl text-body text-grey-900">
            Foundations, tokens, and components for the Sanctions Screening App. Light theme.
          </p>
        </header>

        {/* Logo */}
        <section>
          <div className="mb-6 flex items-baseline justify-between">
            <h2 className="text-h3">Logo</h2>
            <span className="text-tiny text-grey-500">/brand/*.svg</span>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="card">
              <Logo variant="default" className="h-7 w-auto" />
              <p className="mt-6 text-tiny text-grey-500">variant=&quot;default&quot;</p>
            </div>
            <div className="rounded-2xl bg-black p-10">
              <Logo variant="white" className="h-7 w-auto" />
              <p className="mt-6 text-tiny text-grey-500">variant=&quot;white&quot;</p>
            </div>
          </div>
        </section>

        {/* Iconography */}
        <section>
          <div className="mb-6 flex items-baseline justify-between">
            <h2 className="text-h3">Iconography</h2>
            <span className="text-tiny text-grey-500">Material Symbols · Outlined</span>
          </div>

          <div className="card">
            <div className="flex flex-col gap-6">
              <p className="text-small text-grey-900">
                Variable font loaded from Google Fonts. Color inherits via{" "}
                <code className="rounded bg-grey-100 px-1">currentColor</code>; size, weight, and
                FILL axis are controlled by the{" "}
                <code className="rounded bg-grey-100 px-1">&lt;Icon&gt;</code> primitive.
              </p>

              <div>
                <p className="mb-3 text-tiny text-grey-500">size — 20 / 24 / 40 / 48</p>
                <div className="flex items-end gap-6 text-grey-900">
                  {([20, 24, 40, 48] as const).map((size) => (
                    <div key={size} className="flex flex-col items-center gap-1">
                      <Icon name="search" size={size} />
                      <span className="text-tiny text-grey-500">{size}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-3 text-tiny text-grey-500">weight — 300 / 400 / 500 / 700</p>
                <div className="flex items-center gap-6 text-grey-900">
                  {([300, 400, 500, 700] as const).map((weight) => (
                    <div key={weight} className="flex flex-col items-center gap-1">
                      <Icon name="notifications" size={24} weight={weight} />
                      <span className="text-tiny text-grey-500">{weight}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-3 text-tiny text-grey-500">filled — outline / filled</p>
                <div className="flex items-center gap-6 text-grey-900">
                  {[false, true].map((filled) => (
                    <div key={String(filled)} className="flex flex-col items-center gap-1">
                      <Icon name="favorite" size={24} filled={filled} />
                      <span className="text-tiny text-grey-500">
                        {filled ? "filled=true" : "filled=false"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-3 text-tiny text-grey-500">color — inherits via currentColor</p>
                <div className="flex items-center gap-6">
                  <Icon name="check_circle" size={24} className="text-green-500" />
                  <Icon name="error" size={24} className="text-red-600" />
                  <Icon name="info" size={24} className="text-grey-500" />
                  <Icon name="warning" size={24} className="text-yellow-500" />
                  <Icon name="bolt" size={24} className="text-black" />
                </div>
              </div>

              <div>
                <p className="mb-3 text-tiny text-grey-500">common app icons (size 24)</p>
                <div className="grid grid-cols-4 gap-4 sm:grid-cols-6 md:grid-cols-8 text-grey-900">
                  {[
                    "search",
                    "menu",
                    "close",
                    "arrow_back",
                    "arrow_forward",
                    "settings",
                    "person",
                    "notifications",
                    "home",
                    "dashboard",
                    "filter_list",
                    "more_vert",
                    "edit",
                    "delete",
                    "add",
                    "check",
                  ].map((name) => (
                    <div key={name} className="flex flex-col items-center gap-1">
                      <Icon name={name} size={24} />
                      <span className="text-tiny text-grey-500">{name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Color */}
        <section className="space-y-4">
          <div className="flex items-baseline justify-between">
            <h2 className="text-h3">Color</h2>
            <span className="text-tiny text-grey-500">@theme tokens</span>
          </div>
          <ColorRow title="Neutrals" swatches={NEUTRALS} />
          <ColorRow title="Brand · green" swatches={GREENS} />
          <ColorRow title="Greenish grey" swatches={GREENISH_GREYS} />
          <ColorRow title="Status · red" swatches={REDS} />
          <ColorRow title="Status · yellow" swatches={YELLOWS} />
          <ColorRow title="Alpha overlays" swatches={ALPHAS} />
        </section>

        {/* Typography */}
        <section>
          <div className="mb-6 flex items-baseline justify-between">
            <h2 className="text-h3">Typography</h2>
            <span className="text-tiny text-grey-500">Inter · Google Fonts</span>
          </div>

          <div className="card">
            <ul className="divide-y divide-grey-200">
              {TYPE_ROWS.map((row) => (
                <li
                  key={row.label}
                  className={`${row.className} flex items-center gap-6 py-3 text-black`}
                >
                  <span className="flex-1 text-right">{row.label}</span>
                  <span aria-hidden className="text-grey-300">
                    |
                  </span>
                  <span className="flex-1">{row.spec}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Effects */}
        <section>
          <div className="mb-6 flex items-baseline justify-between">
            <h2 className="text-h3">Effects</h2>
            <span className="text-tiny text-grey-500">@theme tokens</span>
          </div>
          <div className="rounded-2xl bg-grey-50 px-6 py-8">
            <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2 lg:grid-cols-3">
              {EFFECTS.map((effect) => (
                <EffectCard key={effect.name} effect={effect} />
              ))}
            </div>
          </div>
        </section>

        {/* Buttons */}
        <section>
          <div className="mb-6 flex items-baseline justify-between">
            <h2 className="text-h3">Buttons</h2>
            <span className="text-tiny text-grey-500">components/button.tsx</span>
          </div>

          {(["md", "sm"] as ButtonSize[]).map((size) => (
            <div key={size} className="mb-8">
              <h3 className="mb-3 text-small text-grey-900">size={`"${size}"`}</h3>
              <div className="overflow-x-auto card">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="border-b border-grey-200 p-4 text-left text-tiny text-grey-500">
                        state \ hierarchy
                      </th>
                      {BUTTON_HIERARCHIES.map((h) => (
                        <th
                          key={h}
                          className="border-b border-grey-200 p-4 text-left text-tiny text-grey-500"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {BUTTON_STATES.map((state) => (
                      <tr key={state}>
                        <td className="border-b border-grey-200 p-4 text-tiny text-grey-500 last:border-b-0">
                          {state}
                        </td>
                        {BUTTON_HIERARCHIES.map((h) => (
                          <td key={h} className="border-b border-grey-200 p-4 last:border-b-0">
                            <Button
                              hierarchy={h}
                              size={size}
                              state={state}
                              leftIcon={<ArrowLeft className="size-5" />}
                              rightIcon={<ArrowRight className="size-5" />}
                            >
                              Button
                            </Button>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <div className="mt-8 card">
            <h3 className="mb-4 text-small text-grey-900">
              Live (interactive · uses native :hover / :active / :disabled)
            </h3>
            <div className="flex flex-wrap items-center gap-3">
              {BUTTON_HIERARCHIES.map((h) => (
                <Button
                  key={h}
                  hierarchy={h}
                  size="md"
                  leftIcon={<ArrowLeft className="size-5" />}
                  rightIcon={<ArrowRight className="size-5" />}
                >
                  Button
                </Button>
              ))}
            </div>
          </div>
        </section>

        {/* Inputs */}
        <section>
          <div className="mb-6 flex items-baseline justify-between">
            <h2 className="text-h3">Input fields</h2>
            <span className="text-tiny text-grey-500">components/field.tsx</span>
          </div>

          <div className="card">
            <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2 lg:grid-cols-3">
              {FIELD_STATES.map((s) => (
                <div key={s} className="space-y-2">
                  <p className="text-tiny text-grey-500">state=&ldquo;{s}&rdquo;</p>
                  <Field
                    id={`field-${s}`}
                    label="Label"
                    hint="Hint text goes here"
                    state={s}
                    placeholder="Placeholder"
                    defaultValue={s === "filled" ? "Placeholder" : undefined}
                    leftIcon={<ArrowLeft className="size-5" />}
                    rightIcon={<ArrowRight className="size-5" />}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 card">
            <h3 className="mb-4 text-small text-grey-900">
              Live (interactive · uses native :hover / :focus-within / :disabled)
            </h3>
            <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2 lg:grid-cols-3">
              <Field
                id="field-live-default"
                label="Default"
                hint="Click into me to see focused state"
                placeholder="Placeholder"
                leftIcon={<ArrowLeft className="size-5" />}
                rightIcon={<ArrowRight className="size-5" />}
              />
              <Field
                id="field-live-unit"
                label="With unit"
                hint="Unit on the trailing edge"
                placeholder="0"
                unit="bags"
              />
              <Field
                id="field-live-error"
                label="Error"
                error="Hint text goes here"
                placeholder="Placeholder"
                leftIcon={<ArrowLeft className="size-5" />}
                rightIcon={<ArrowRight className="size-5" />}
              />
            </div>
          </div>
        </section>

        {/* Toggle */}
        <section>
          <div className="mb-6 flex items-baseline justify-between">
            <h2 className="text-h3">Toggle</h2>
            <span className="text-tiny text-grey-500">components/toggle.tsx</span>
          </div>

          <div className="card">
            <div className="grid grid-cols-2 gap-x-12 gap-y-6 md:grid-cols-4">
              <div className="space-y-2">
                <p className="text-tiny text-grey-500">off</p>
                <Toggle defaultChecked={false} />
              </div>
              <div className="space-y-2">
                <p className="text-tiny text-grey-500">on</p>
                <Toggle defaultChecked />
              </div>
              <div className="space-y-2">
                <p className="text-tiny text-grey-500">off · disabled</p>
                <Toggle defaultChecked={false} disabled />
              </div>
              <div className="space-y-2">
                <p className="text-tiny text-grey-500">on · disabled</p>
                <Toggle defaultChecked disabled />
              </div>
            </div>
          </div>
        </section>

        {/* Tag */}
        <section>
          <div className="mb-6 flex items-baseline justify-between">
            <h2 className="text-h3">Tag</h2>
            <span className="text-tiny text-grey-500">components/tag.tsx</span>
          </div>

          <div className="card flex flex-col gap-6">
            {(["sm", "md"] as TagSize[]).map((size) => (
              <div key={size} className="flex flex-col gap-3">
                <h3 className="text-small text-grey-900">size={`"${size}"`}</h3>
                <div className="flex flex-wrap items-center gap-2">
                  {(["neutral", "positive", "negative"] as TagTone[]).map((tone) => (
                    <Tag key={tone} tone={tone} size={size}>
                      {tone === "neutral"
                        ? "Last 30 Days"
                        : tone === "positive"
                          ? "Up to date"
                          : "Requires action"}
                    </Tag>
                  ))}
                  <Tag
                    size={size}
                    leadingIcon={<Icon name="bolt" size={size === "sm" ? 20 : 20} />}
                  >
                    With icon
                  </Tag>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Layout & spacing */}
        <section>
          <div className="mb-6 flex items-baseline justify-between">
            <h2 className="text-h3">Layout & spacing</h2>
            <span className="text-tiny text-grey-500">@utility card / popup</span>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* body */}
            <div className="space-y-2">
              <p className="text-tiny text-grey-500">body — padding 12 · gap 8</p>
              <div className="rounded-[4px] border border-dashed border-grey-300 bg-grey-25 p-3">
                <div className="flex flex-col gap-2">
                  <div className="h-6 w-2/3 rounded-[4px] bg-grey-200" />
                  <div className="h-6 w-1/2 rounded-[4px] bg-grey-200" />
                  <div className="h-6 w-3/4 rounded-[4px] bg-grey-200" />
                </div>
              </div>
            </div>

            {/* card */}
            <div className="space-y-2">
              <p className="text-tiny text-grey-500">
                <code>card</code> — padding 16 · gap 16
              </p>
              <div className="card">
                <div className="flex flex-col gap-4">
                  <div className="h-6 w-2/3 rounded-[4px] bg-grey-200" />
                  <div className="h-6 w-1/2 rounded-[4px] bg-grey-200" />
                  <div className="h-6 w-3/4 rounded-[4px] bg-grey-200" />
                </div>
              </div>
            </div>

            {/* popup — standalone (e.g., login screen on grey-25) */}
            <div className="space-y-2">
              <p className="text-tiny text-grey-500">
                <code>popup</code> · standalone — padding 32 · gap 16 · over plain bg, keep{" "}
                <code>shadow-flat</code>
              </p>
              <div className="rounded-[4px] bg-grey-25 p-6">
                <div className="popup">
                  <div className="flex flex-col gap-4">
                    <div className="h-6 w-2/3 rounded-[4px] bg-grey-200" />
                    <div className="h-6 w-1/2 rounded-[4px] bg-grey-200" />
                    <div className="h-6 w-3/4 rounded-[4px] bg-grey-200" />
                  </div>
                </div>
              </div>
            </div>

            {/* popup — modal (over existing UI, grey-900-24 backdrop, soft shadow) */}
            <div className="space-y-2">
              <p className="text-tiny text-grey-500">
                <code>popup</code> · modal — over content, add <code>bg-grey-900-24</code> backdrop
                &amp; <code>shadow-soft</code>
              </p>
              <div className="relative overflow-hidden rounded-[4px] bg-white p-6">
                {/* simulated underlying content */}
                <div className="flex flex-col gap-2 opacity-60">
                  <div className="h-6 w-2/3 rounded-[4px] bg-grey-200" />
                  <div className="h-6 w-1/2 rounded-[4px] bg-grey-200" />
                  <div className="h-6 w-3/4 rounded-[4px] bg-grey-200" />
                  <div className="h-6 w-2/3 rounded-[4px] bg-grey-200" />
                </div>
                {/* overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-grey-900-24">
                  <div className="popup shadow-soft min-w-[60%]">
                    <div className="flex flex-col gap-4">
                      <div className="h-6 w-2/3 rounded-[4px] bg-grey-200" />
                      <div className="h-6 w-1/2 rounded-[4px] bg-grey-200" />
                      <div className="h-6 w-3/4 rounded-[4px] bg-grey-200" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
